/**
 * Per-voxel colour, which is the whole look.
 *
 * The references are a flat-colour micro-voxel world: no textures anywhere, and
 * every surface is dozens of slightly different shades of one hue. A cave wall
 * is forty browns, a meadow is twenty greens. Rendering one uniform colour per
 * block type — which is what a texture atlas gives you — produces flat plastic
 * mush no matter how good the lighting is.
 *
 * ## Why the shade is coherent rather than per voxel
 *
 * Greedy meshing merges runs of voxels that agree on everything. Independent
 * per-voxel jitter makes every voxel disagree, so nothing merges and the vertex
 * count explodes. Sampling the shade over a 2x2x2 cell keeps short runs
 * mergeable *and* reads better: the references show mottled patches, not
 * per-voxel static.
 */

/** Discrete shades per block. More is smoother; fewer merges better. */
export const SHADE_COUNT = 8;

/** Edge length in voxels of one constant-shade cell. */
const SHADE_CELL = 2;

export interface VoxelColor {
  /** Base sRGB, 0-255. */
  readonly rgb: readonly [number, number, number];
  /** Peak-to-peak brightness spread across the shades, as a fraction of base. */
  readonly jitter: number;
  /**
   * Shade per *column* rather than per 2x2x2 cell.
   *
   * For anything built as one-voxel towers — grass blades, flowers — the cell
   * shade makes neighbouring blades share a colour and the meadow flattens to
   * two or three greens. Keyed on x/z alone each blade gets its own shade and
   * still merges cleanly up its own height, which is the only direction a
   * blade has to merge in.
   */
  readonly columnShade?: boolean;
  /**
   * Bends in the wind. Grass in the references is never still, and a meadow of
   * rigid spikes reads as plastic however well it is coloured.
   */
  readonly sway?: boolean;
}

function color(hex: number, jitter: number, columnShade = false, sway = false): VoxelColor {
  return Object.freeze({
    rgb: Object.freeze([(hex >> 16) & 0xff, (hex >> 8) & 0xff, hex & 0xff] as const),
    jitter,
    columnShade,
    sway,
  });
}

/**
 * Solved against the reference stills rather than eyeballed, because eyeballing
 * an *albedo* is guessing: what the eye judges is the albedo after the shader
 * has multiplied it by the hemisphere ambient, the sun and the AO term, and
 * that product is nowhere near the hex written here.
 *
 * ## Why these are so much more olive than they look
 *
 * Measured over all seven vibeshots, every green in the references falls in a
 * narrow band: hue 63-79, saturation 55-66%, value 25-48%. The palette that
 * preceded this one rendered at hue 90-110 — a true blue-leaning green — and
 * the whole world read cold and synthetic against them. Two things caused it:
 * the albedos themselves sat at hue 80-107, and the sky ambient was a strong
 * blue that dragged the product further round.
 *
 * So these were solved backwards from the shader: for each block, search albedo
 * HSV for the one whose *rendered* colour under its own typical light lands on
 * the reference band, rejecting anything that clips once the shade jitter is
 * applied. Canopy is held ~10 value points under meadow grass and ~6 degrees
 * further round, which is the separation the references show between a crown
 * and the ground it stands on — solved together, they came out nearly the same
 * colour and the treeline vanished into the field.
 *
 * Anything here that changes has to be re-solved against the lighting in
 * BlockMaterial.ts, not nudged: the two are one calibration.
 *
 * Ground and rock carry the widest spread because that is where the eye reads
 * the mottling; flowers carry almost none, since they are small accents that
 * should stay one readable colour.
 */
export const BLOCK_COLORS: Readonly<Record<string, VoxelColor>> = Object.freeze({
  // Warm mid-grey, not the cool 0x8b8b90 it was. Under a blue-tinted ambient a
  // neutral grey rendered at hue 216 — lavender rock sitting in a green field.
  stone: color(0x7d7872, 0.22),
  cobblestone: color(0x75706b, 0.24),
  dirt: color(0x755e49, 0.26),
  grass: color(0x5a632c, 0.24),
  plains_grass: color(0x6a6e32, 0.22),
  sand: color(0xd8c68d, 0.16),
  gravel: color(0x7a756d, 0.24),
  water: color(0x1d6f76, 0.10),
  glass: color(0xcfe3ea, 0.06),
  oak_log: color(0x6d4c2e, 0.20),
  oak_leaves: color(0x676e36, 0.30),
  birch_log: color(0xc8bfa8, 0.16),
  birch_leaves: color(0x7b8240, 0.30),
  pine_log: color(0x5a3f28, 0.20),
  pine_leaves: color(0x49592e, 0.30),
  forest_ground_cover: color(0x525e29, 0.28),
  grass_blade: color(0x5a6129, 0.42, true, true),
  flower_violet: color(0x8a5fc0, 0.08, true, true),
  flower_red: color(0xc0392b, 0.08, true, true),
  flower_orange: color(0xe08a2a, 0.08, true, true),
  coal_ore: color(0x4a4a4e, 0.18),
  iron_ore: color(0xa08a72, 0.18),
  copper_ore: color(0x9a7a52, 0.18),
  torch: color(0xffcf7a, 0.04),
  workbench: color(0x8a6238, 0.16),
  chest: color(0x8a6238, 0.16),
});

/** Anything without an authored colour, so a missing entry is visible, not silent. */
export const FALLBACK_COLOR: VoxelColor = color(0xff00ff, 0);

/**
 * Shade index for a world voxel. Constant across a SHADE_CELL cube so greedy
 * runs survive; hashed so it is stable per world position and identical in
 * every worker.
 */
export function shadeIndexAt(x: number, y: number, z: number, columnShade = false): number {
  const cx = columnShade ? Math.floor(x) : Math.floor(x / SHADE_CELL);
  const cy = columnShade ? 0 : Math.floor(y / SHADE_CELL);
  const cz = columnShade ? Math.floor(z) : Math.floor(z / SHADE_CELL);
  let h = (cx * 0x27d4eb2d) ^ (cy * 0x165667b1) ^ (cz * 0x9e3779b9);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) % SHADE_COUNT;
}

/** Applies a shade to a base colour, returning 0-255 channels. */
export function shadeColor(base: VoxelColor, shade: number): [number, number, number] {
  // -0.5..+0.5 across the shades, so the base colour stays the mean.
  const offset = (shade / (SHADE_COUNT - 1) - 0.5) * base.jitter;
  const scale = 1 + offset;
  return [
    Math.max(0, Math.min(255, Math.round(base.rgb[0] * scale))),
    Math.max(0, Math.min(255, Math.round(base.rgb[1] * scale))),
    Math.max(0, Math.min(255, Math.round(base.rgb[2] * scale))),
  ];
}

export function colorForBlockName(name: string): VoxelColor {
  return BLOCK_COLORS[name] ?? FALLBACK_COLOR;
}
